// Every React Query cache key, in one place.
//
// Inline key arrays at the call sites would be a silent failure mode: an
// invalidation whose key does not match a query's key does not error, it
// simply does not refetch, and the UI shows stale data until the next mount.
// A registry turns that typo into a compile error.
//
// The shape follows React Query's prefix matching: `qk.trades.all` is
// `["trades"]`, which invalidates every `qk.trades.list(filters)` beneath it.

export const qk = {
  // ---- calendars / events ----
  calendars: { all: ["calendars"] as const },
  events: {
    all: ["events"] as const,
    range: (from?: string, to?: string) => ["events", from, to] as const,
    detail: (id: number) => ["events", "detail", id] as const,
  },

  // ---- tasks ----
  tasks: {
    all: ["tasks"] as const,
    list: (status?: string) => ["tasks", status ?? "all"] as const,
  },

  // ---- guided imports ----
  imports: {
    all: ["imports"] as const,
    sources: ["imports", "sources"] as const,
    entities: (kind: string) => ["imports", "entities", kind] as const,
    records: (runId: number) => ["imports", "records", runId] as const,
  },

  // ---- extensions (the marketplace roster, not an import concern) ----
  extensions: ["extensions"] as const,

  // ---- accounts / balances ----
  accounts: {
    all: ["accounts"] as const,
    /** The accounts with transactions, for a filter that can only return rows. */
    active: ["accounts", "active"] as const,
    detail: (id: number) => ["accounts", "detail", id] as const,
    footprint: (id: number) => ["accounts", "detail", id, "footprint"] as const,
    activity: (id: number) => ["accounts", "detail", id, "activity"] as const,
  },
  interest: {
    all: ["interest"] as const,
    list: (accountIds?: readonly number[]) =>
      ["interest", accountIds?.length ? accountIds.join(",") : "all"] as const,
  },
  // The records of the accounts in scope, one stream each.
  observations: {
    list: (ids: readonly number[]) => ["balances", "observations", ids.join(",")] as const,
  },
  positions: {
    all: ["positions"] as const,
    list: (ids: readonly number[]) => ["positions", ids.join(",")] as const,
  },
  lots: {
    all: ["lots"] as const,
    list: (ids: readonly number[]) => ["lots", ids.join(",")] as const,
  },
  // One record a hand-off from another page opens.
  records: {
    one: (kind: string, id: number | undefined) => ["records", kind, id] as const,
  },
  balances: {
    all: ["balances"] as const,
    fxRates: ["balances", "fx-rates"] as const,
    netWorth: ["balances", "net-worth"] as const,
  },

  institutions: {
    all: ["institutions"] as const,
  },
  currencies: {
    all: ["currencies"] as const,
  },

  // ---- exports ----
  exports: {
    catalog: ["exports", "catalog"] as const,
    dataset: (filename: string) => ["exports", "dataset", filename] as const,
  },

  // ---- transactions ledger ----
  transactions: {
    all: ["transactions"] as const,
    list: (k: readonly unknown[]) => ["transactions", ...k] as const,
  },

  // ---- investments ----
  trades: {
    all: ["trades"] as const,
    list: (k: readonly unknown[]) => ["trades", ...k] as const,
  },
  dividends: {
    all: ["dividends"] as const,
    list: (k: readonly unknown[]) => ["dividends", ...k] as const,
  },
  portfolio: { all: ["portfolio"] as const, episodes: ["portfolio", "episodes"] as const },
  portfolioPerformance: { all: ["portfolio-performance"] as const },
  fxAttribution: { all: ["fx-attribution"] as const },
  fxPerformance: { all: ["fx-performance"] as const },

  // ---- cash flow ----
  cashflow: {
    all: ["cashflow"] as const,
    flows: ["cashflow", "flows"] as const,
  },

  // ---- expense taxonomy ----
  categories: { all: ["categories"] as const },

  // ---- markets ----
  watchlist: { all: ["watchlist"] as const },
  marketSource: { all: ["market-source"] as const },
  marketSignal: { all: ["market-signal"] as const },
  marketNews: { all: ["market-news"] as const },

  // ---- health & fitness ----
  healthDays: {
    all: ["health-days"] as const,
    range: (from?: string, to?: string) => ["health-days", from, to] as const,
  },
  healthEntries: {
    all: ["health-entries"] as const,
    // `null` while no day is picked — the query is disabled, but the key is still built.
    day: (date: string | null) => ["health-entries", date] as const,
  },
  nutrition: { all: ["nutrition"] as const },
  mealPresets: { all: ["meal-presets"] as const },

  // ---- feeds (RSS/Atom subscriptions) ----
  feedCategories: { all: ["feed-categories"] as const },
  feeds: { all: ["feeds"] as const },
  feedEntries: {
    all: ["feed-entries"] as const,
    list: (k: readonly unknown[]) => ["feed-entries", ...k] as const,
  },

  // ---- the host's services ----
  backup: { status: ["backup", "status"] as const },

  // ---- notifications ----
  notifications: {
    all: ["notifications"] as const,
    list: (k: readonly unknown[]) => ["notifications", ...k] as const,
    unreadCount: ["notifications", "unread-count"] as const,
  },

  // ---- inspiration (a daily curated quote + artwork) ----
  inspiration: {
    all: ["inspiration"] as const,
    // No date in these keys: the server owns the day (from the display-timezone
    // setting), so the browser has no date to key on.
    today: ["inspiration", "today"] as const,
    days: (filter: string) => ["inspiration", "days", filter] as const,
    progress: ["inspiration", "progress"] as const,
  },

  // ---- reasoning: LLM providers + Athena chat ----
  llmProviders: { all: ["llm-providers"] as const },
  chatConversations: {
    all: ["chat-conversations"] as const,
    messages: (conversationId: number) =>
      ["chat-conversations", conversationId, "messages"] as const,
  },

  // ---- app settings ----
  settings: {
    all: ["settings"] as const,
    about: ["settings", "about"] as const,
    displayTimezone: ["settings", "display-timezone"] as const,
    holidayCountries: ["settings", "holiday-countries"] as const,
    holidayLocations: ["settings", "holiday-locations"] as const,
    holidaySubdivisions: (countryCode: string) =>
      ["settings", "holiday-subdivisions", countryCode] as const,
    feedPollInterval: ["settings", "feed-poll-interval"] as const,
    feedAutoReadDelay: ["settings", "feed-auto-read-delay"] as const,
    feedEntryRetentionDays: ["settings", "feed-entry-retention-days"] as const,
    feedsUiState: ["settings", "feeds-ui-state"] as const,
    cashFlowComparison: ["settings", "cash-flow-comparison"] as const,
    defaultImportCalendarId: ["settings", "default-import-calendar"] as const,
    dcaDayOfMonth: ["settings", "dca-day-of-month"] as const,
    notificationRetentionDays: ["settings", "notification-retention-days"] as const,
  },

  // ---- strategy ----
  stockTradingWindows: { all: ["stock-trading-windows"] as const },
} as const;
